import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { checkStoredAuth } from '../../store/slices/authSlice';
import { Loading } from '../loading';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Heart, ArrowLeft } from 'lucide-react';

interface AuthWrapperProps {
  children: React.ReactNode;
  onAuthSuccess?: () => void;
}

type AuthMode = 'login' | 'signup' | 'welcome';

export default function AuthWrapper({ children, onAuthSuccess }: AuthWrapperProps) {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAppSelector(state => state.auth);
  const [authMode, setAuthMode] = useState<AuthMode>('welcome');

  useEffect(() => {
    // Check for stored authentication on component mount
    dispatch(checkStoredAuth());
  }, [dispatch]);

  // Show loading while checking stored auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loading />
      </div>
    );
  }

  // If authenticated, show the main app
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Handle successful authentication
  const handleAuthSuccess = () => {
    if (onAuthSuccess) {
      onAuthSuccess();
    }
  };

  // Show welcome screen by default
  if (authMode === 'welcome') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md mx-auto" data-testid="auth-welcome">
          <CardContent className="pt-6">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="p-4 bg-primary/10 rounded-full">
                  <Heart className="h-12 w-12 text-primary" />
                </div>
              </div>
              
              <div className="space-y-2">
                <h1 className="text-3xl font-bold">Couple Expenses</h1>
                <p className="text-muted-foreground">
                  Track your expenses together and reach your financial goals as a team
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={() => setAuthMode('signup')}
                  className="w-full"
                  size="lg"
                  data-testid="get-started-button"
                >
                  Get Started Together
                </Button>
                
                <Button
                  onClick={() => setAuthMode('login')}
                  variant="outline"
                  className="w-full"
                  size="lg"
                  data-testid="login-existing-button"
                >
                  Log In to Existing Account
                </Button>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg text-sm">
                <h3 className="font-medium mb-2">Perfect for couples who want to:</h3>
                <ul className="text-muted-foreground space-y-1 text-left">
                  <li>• Track shared expenses together</li>
                  <li>• Set and achieve financial goals</li>
                  <li>• See spending insights and patterns</li>
                  <li>• Gamify saving with challenges</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show login form
  if (authMode === 'login') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <Button
            variant="ghost"
            onClick={() => setAuthMode('welcome')}
            className="mb-4"
            data-testid="back-to-welcome-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <LoginForm
            onSuccess={handleAuthSuccess}
            onSwitchToSignup={() => setAuthMode('signup')}
          />
        </div>
      </div>
    );
  }

  // Show signup form
  if (authMode === 'signup') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md">
          <Button
            variant="ghost"
            onClick={() => setAuthMode('welcome')}
            className="mb-4"
            data-testid="back-to-welcome-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <SignupForm
            onSuccess={handleAuthSuccess}
            onSwitchToLogin={() => setAuthMode('login')}
          />
        </div>
      </div>
    );
  }

  return null;
}